import { useCallback } from 'react';
import { useDemand, ACTIONS } from '../../context/DemandContext';

/**
 * 🎯 版本控制 Hook
 * 統一管理版本控制相關的邏輯和版本數據
 */
export const useVersionControl = () => {
  const { state, dispatch } = useDemand();
  const { version } = state;

  /**
   * 設置版本數據
   * @param {Object} data - 版本數據 { sourceItem, sourceBlob, sourceJsonFileId, selectedVersion }
   */
  const setVersionData = useCallback((data) => {
    dispatch({
      type: ACTIONS.UPDATE_VERSION,
      payload: data
    });
  }, [dispatch]);

  /**
   * 設置選中的版本
   * @param {number} versionIndex - 版本索引
   */
  const setSelectedVersion = useCallback((versionIndex) => {
    dispatch({
      type: ACTIONS.SET_SELECTED_VERSION,
      payload: versionIndex
    });
  }, [dispatch]);

  /**
   * 更新版本數據
   * @param {Object} updates - 要更新的版本數據
   */
  const updateVersion = useCallback((updates) => {
    dispatch({
      type: ACTIONS.UPDATE_VERSION,
      payload: updates
    });
  }, [dispatch]);

  /**
   * 設置是否允許額外版本
   * @param {boolean} allowed - 是否允許
   */
  const setAllowExtraVersions = useCallback((allowed) => {
    dispatch({
      type: ACTIONS.SET_ALLOW_EXTRA_VERSIONS,
      payload: allowed
    });
  }, [dispatch]);

  /**
   * 更新版本控制資訊
   * @param {Object} updates - 要更新的版本控制資訊
   */
  const updateVersionControl = useCallback((updates) => {
    dispatch({
      type: ACTIONS.UPDATE_VERSION,
      payload: updates
    });
  }, [dispatch]);

  /**
   * 計算下一個版本號
   * @param {Array} sourceItem - 現有版本數據
   * @returns {number} 下一個版本號
   */
  const calculateNextVersionNumber = useCallback((sourceItem = []) => {
    if (!sourceItem?.length) {
      return 1;
    }

    // 計算未來版本號 (當前版本數量 + 1)
    const futureVersionNumber = sourceItem.filter((item) => item.isEdited).length + 1;
    return futureVersionNumber;
  }, []);

  /**
   * 轉換數字為中文數字
   * @param {number} num - 數字
   * @returns {string} 中文數字
   */
  const numToChinese = useCallback((num) => {
    const chineseNum = [
      "一", "二", "三", "四", "五", "六", "七", "八", "九", "十",
    ];
    if (num <= 10) {
      return chineseNum[num - 1];
    }
    if (num < 20) {
      return "十" + (num > 10 ? chineseNum[num - 11] : "");
    }
    return (
      chineseNum[Math.floor(num / 10) - 1] +
      "十" +
      (num % 10 > 0 ? chineseNum[(num % 10) - 1] : "")
    );
  }, []);

  /**
   * 獲取版本文字
   * @returns {string} 版本文字（如：版本一、版本二）
   */
  const getVersionText = useCallback(() => {
    const versionNumber = calculateNextVersionNumber(version.sourceItem);
    return `版本${numToChinese(versionNumber)}`;
  }, [calculateNextVersionNumber, numToChinese, version.sourceItem]);

  /**
   * 檢查版本數量限制
   * @returns {Object} 檢查結果 { isValid, message }
   */
  const checkVersionLimit = useCallback(() => {
    const sourceItem = version.sourceItem;
    // 如果允許額外版本，則不檢查限制
    if (version.allowExtraVersions) {
      return { isValid: true, message: '' };
    }

    // 檢查是否已達版本上限
    if (
      sourceItem?.length >= 3 &&
      sourceItem.every((item) => item.isEdited)
    ) {
      return {
        isValid: false,
        message: "需求單版本數量已達上限，請啟用額外版本功能或刪除舊版本後再試!"
      };
    }

    return { isValid: true, message: '' };
  }, [version.allowExtraVersions, version.sourceItem]);

  /**
   * 根據現有版本初始化版本控制狀態
   */
  const initializeVersionControl = useCallback(() => {
    const sourceItem = version.sourceItem;
    const nextVersionNumber = calculateNextVersionNumber(sourceItem);
    const totalVersions = sourceItem?.length || 0;
    const isCreatedByBackend = sourceItem?.length > 0 ? sourceItem[0].isCreatedByBackend : true;

    updateVersionControl({
      currentVersionNumber: nextVersionNumber,
      totalVersions,
      isCreatedByBackend,
      isEdited: true // 新版本默認為已編輯狀態
    });
  }, [calculateNextVersionNumber, updateVersionControl, version.sourceItem]);

  /**
   * 準備版本數據用於保存
   * @param {Object} currentData - 當前數據 { basic, ceremony, notes, live }
   * @returns {Object} 準備好的版本數據
   */
  const prepareVersionData = useCallback((currentData) => {
    const sourceItem = version.sourceItem;
    const sourceData = {
      version: [],
      allowExtraVersions: version.allowExtraVersions,
    };

    // 準備當前版本數據
    const currentVersion = {
      isEdited: true,
      isCreatedByBackend: version.isCreatedByBackend,
      basic: currentData.basic,
      ceremony: currentData.ceremony,
      notes: currentData.notes || '',
      price: currentData.price || 0,
      live: {
        isEnabled: currentData.live?.isEnabled || false,
        roomCode: currentData.live?.roomCode || null,
        url: currentData.live?.url || null
      }
    };

    // 處理版本控制邏輯
    if (!sourceItem || sourceItem.length === 0) {
      // 情況1: 無現有版本，創建第一個版本
      currentVersion.isEdited = false; // 從後台新增的版本一, 設置成未編輯
      sourceData.version.push(currentVersion);
    } else {
      // 先保留所有已存在的版本
      sourceItem.forEach((item) => {
        if (item.isEdited) {
          sourceData.version.push(item);
        }
      });

      // 添加當前版本
      sourceData.version.push(currentVersion);

      // 只有在不允許額外版本的情況下才限制版本數量
      if (!version.allowExtraVersions && sourceData.version.length > 3) {
        sourceData.version = sourceData.version.slice(-3);
      }
    }

    return sourceData;
  }, [version.allowExtraVersions, version.isCreatedByBackend, version.sourceItem]);

  return {
    // 整合後的版本狀態
    version,
    
    // 快捷訪問屬性（向後兼容）
    allowExtraVersions: version.allowExtraVersions,
    currentVersionNumber: version.currentVersionNumber,
    totalVersions: version.totalVersions,
    isEdited: version.isEdited,
    isCreatedByBackend: version.isCreatedByBackend,
    sourceItem: version.sourceItem,
    sourceBlob: version.sourceBlob,
    sourceJsonFileId: version.sourceJsonFileId,
    selectedVersion: version.selectedVersion,

    // 操作方法
    setAllowExtraVersions,
    updateVersionControl,
    initializeVersionControl,

    // 計算方法
    calculateNextVersionNumber,
    getVersionText,
    checkVersionLimit,
    prepareVersionData,

    // 工具方法
    numToChinese,

    // 版本數據方法
    setVersionData,
    setSelectedVersion,
    updateVersion
  };
}; 